import React from 'react';
import Button from 'design-system/src/components/Button/Button';
import { ErrorMessage, ErrorMessageProps } from 'design-system/src/components/ErrorMessage/ErrorMessage';
import { PROVIDER_SUPPORT_CONTACT_INFO } from '../../../constants';

export type SignupErrorMessageProps = Omit<ErrorMessageProps, 'children'> & {
  /**
   * The error string from the backend. If it matches a known error code,
   * a rich message with a mailto link is rendered; otherwise the raw string is shown.
   */
  error: string;
};

const SupportEmailLink = () => (
  <Button
    variant="link"
    href={`mailto:${PROVIDER_SUPPORT_CONTACT_INFO.email}`}
    className="!whitespace-nowrap !text-red-400 hover:!text-red-500"
  >
    {PROVIDER_SUPPORT_CONTACT_INFO.email}
  </Button>
);

const GenericFlowError = ({ flow }: { flow: string }) => (
  <>
    We couldn't complete your {flow} at this time. Please try again or contact support at <SupportEmailLink />.
  </>
);

const ERROR_CODE_MESSAGES: Record<string, React.ReactNode> = {
  GENERIC_SIGNUP_ERROR: <GenericFlowError flow="signup" />,
  GENERIC_SETUP_ERROR: <GenericFlowError flow="account setup" />,
};

export const SignupErrorMessage: React.FC<SignupErrorMessageProps> = ({ error, ...errorMessageProps }) => {
  // Use hasOwnProperty to avoid matching inherited properties (e.g., "toString", "constructor")
  const richMessage = Object.prototype.hasOwnProperty.call(ERROR_CODE_MESSAGES, error)
    ? ERROR_CODE_MESSAGES[error]
    : null;

  return <ErrorMessage {...errorMessageProps}>{richMessage ?? error}</ErrorMessage>;
};
